import { useSpring, useTransform, MotionValue, motionValue } from "framer-motion";

interface ParallaxConfig {
  stiffness?: number;
  damping?: number;
}

/**
 * Hook personalizado para criar efeitos de parallax com scrolling suave
 * @param scrollY Valor de scroll (pode ser um número ou MotionValue)
 * @param config Configurações opcionais para ajustar o comportamento da mola
 * @returns Um objeto com valores de transformação para efeitos parallax
 */
export const useParallax = (
  scrollYInput: number | MotionValue<number>,
  config: ParallaxConfig = { stiffness: 50, damping: 20 }
) => {
  // Garante que estamos trabalhando com MotionValue para evitar chamadas condicionais
  // Isso segue a regra de hooks que devem ser chamados na mesma ordem sempre
  const scrollY = typeof scrollYInput === 'number' ? motionValue(scrollYInput) : scrollYInput;
  
  // Aplicamos o spring de forma não-condicional 
  const smoothScroll = useSpring(scrollY, {
    stiffness: config.stiffness || 50,
    damping: config.damping || 20
  });

  // Valores positivos - movimento na mesma direção do scroll
  const fastest = useTransform(smoothScroll, (y) => y * 0.5);    // 50% da velocidade de scroll
  const faster = useTransform(smoothScroll, (y) => y * 0.3);     // 30% da velocidade de scroll
  const fast = useTransform(smoothScroll, (y) => y * 0.2);       // 20% da velocidade de scroll
  const medium = useTransform(smoothScroll, (y) => y * 0.1);     // 10% da velocidade de scroll
  const slow = useTransform(smoothScroll, (y) => y * 0.05);      // 5% da velocidade de scroll
  const slower = useTransform(smoothScroll, (y) => y * 0.03);    // 3% da velocidade de scroll
  const slowest = useTransform(smoothScroll, (y) => y * 0.01);   // 1% da velocidade de scroll
  
  // Valores negativos - movimento na direção oposta ao scroll
  const reverseFastest = useTransform(smoothScroll, (y) => y * -0.5);   // -50% da velocidade
  const reverseFaster = useTransform(smoothScroll, (y) => y * -0.3);    // -30% da velocidade
  const reverseFast = useTransform(smoothScroll, (y) => y * -0.2);      // -20% da velocidade
  const reverseMedium = useTransform(smoothScroll, (y) => y * -0.15);   // -15% da velocidade
  const reverse = useTransform(smoothScroll, (y) => y * -0.1);          // -10% da velocidade
  const reverseSlow = useTransform(smoothScroll, (y) => y * -0.05);     // -5% da velocidade
  const reverseSlower = useTransform(smoothScroll, (y) => y * -0.03);   // -3% da velocidade
  const reverseSlowest = useTransform(smoothScroll, (y) => y * -0.01);  // -1% da velocidade

  return {
    // Valores positivos
    fastest,
    faster,
    fast,
    medium,
    slow,
    slower,
    slowest,
    
    // Valores negativos (direção oposta)
    reverseFastest,
    reverseFaster,
    reverseFast,
    reverseMedium,
    reverse,
    reverseSlow,
    reverseSlower,
    reverseSlowest,
    
    // O scroll suavizado para uso direto
    smoothScroll,
  };
};